import { supabase } from './supabaseClient';

// Los datos de "Mi Empresa" se guardan en la tabla oferta_empresas, identificados por el
// correo de la sesión del portal, para recuperarlos al entrar desde otra PC. El navegador
// conserva siempre una copia: si Supabase no responde, la pantalla sigue funcionando.
export const TIPOS_EMPRESA = ['PJ', 'PN'];

const claveLocal = tipo => 'nuvia_empresa_' + String(tipo).toLowerCase();
const clavePendiente = tipo => 'nuvia_empresa_pendiente_' + String(tipo).toLowerCase();

const esObjeto = valor => !!valor && typeof valor === 'object' && !Array.isArray(valor);

export const normalizarCorreo = value => String(value || '').trim().toLowerCase();

export function leerEmpresaLocal(tipo) {
  try {
    const datos = JSON.parse(localStorage.getItem(claveLocal(tipo)) || 'null');
    return esObjeto(datos) ? datos : null;
  } catch { return null; }
}

export function leerEmpresasLocales() {
  return Object.fromEntries(
    TIPOS_EMPRESA.map(tipo => [tipo, leerEmpresaLocal(tipo)]).filter(([, datos]) => datos)
  );
}

function guardarEmpresaLocal(tipo, datos) {
  try { localStorage.setItem(claveLocal(tipo), JSON.stringify(datos)); } catch {}
}

// Un cambio guardado sin conexión queda pendiente y sube en la siguiente sincronización.
function marcarPendiente(tipo, pendiente) {
  try {
    if (pendiente) localStorage.setItem(clavePendiente(tipo), '1');
    else localStorage.removeItem(clavePendiente(tipo));
  } catch {}
}

function estaPendiente(tipo) {
  try { return localStorage.getItem(clavePendiente(tipo)) === '1'; } catch { return false; }
}

async function leerNube(correo) {
  const { data, error } = await supabase
    .from('oferta_empresas')
    .select('tipo, datos')
    .eq('correo', correo);
  if (error) throw new Error(error.message || 'No se pudo consultar la cuenta');
  return Object.fromEntries(
    (data || []).filter(fila => esObjeto(fila.datos)).map(fila => [fila.tipo, fila.datos])
  );
}

async function escribirNube(correo, tipo, datos, soloSiNoExiste = false) {
  const { error } = await supabase.from('oferta_empresas').upsert(
    { correo, tipo, datos },
    { onConflict: 'correo,tipo', ignoreDuplicates: soloSiNoExiste }
  );
  if (error) throw new Error(error.message || 'No se pudo guardar en la cuenta');
}

/**
 * Trae las empresas de la cuenta y sube lo que falte desde este navegador.
 * Nunca falla: si la nube no responde devuelve la copia local con enLaNube en false.
 */
export async function sincronizarEmpresas(email) {
  const locales = leerEmpresasLocales();
  const correo = normalizarCorreo(email);
  if (!correo) return { empresas: locales, enLaNube: false };

  let remotas;
  try { remotas = await leerNube(correo); }
  catch { return { empresas: locales, enLaNube: false }; }

  let subioAlgo = false;
  for (const tipo of TIPOS_EMPRESA) {
    const local = locales[tipo];
    if (!local) continue;
    try {
      if (estaPendiente(tipo)) {
        // Cambio explícito del usuario que no llegó a guardarse: manda el equipo.
        await escribirNube(correo, tipo, local, false);
        marcarPendiente(tipo, false);
        subioAlgo = true;
      } else if (!remotas[tipo]) {
        // Datos previos a la cuenta: suben sin pisar lo que exista en la nube.
        await escribirNube(correo, tipo, local, true);
        subioAlgo = true;
      }
    } catch { /* queda para el próximo intento; la copia local no se toca */ }
  }

  if (subioAlgo) {
    try { remotas = await leerNube(correo); } catch { /* nos quedamos con lo leído antes */ }
  }

  const empresas = { ...locales, ...remotas };
  TIPOS_EMPRESA.forEach(tipo => { if (empresas[tipo]) guardarEmpresaLocal(tipo, empresas[tipo]); });
  return { empresas, enLaNube: true };
}

/**
 * Deja vacíos los datos de un tipo, en el equipo y en la cuenta.
 * Las políticas de oferta_empresas no dejan borrar filas con la clave anon, así que la
 * fila se vacía con un objeto sin campos: el formulario vuelve en blanco en cualquier PC.
 */
export async function limpiarEmpresa(email, tipo) {
  return guardarEmpresa(email, tipo, {});
}

/**
 * Guarda siempre en el equipo y, si se puede, en la cuenta.
 * Devuelve enLaNube en false cuando quedó solo en el equipo: nunca pierde lo escrito.
 */
export async function guardarEmpresa(email, tipo, datos) {
  guardarEmpresaLocal(tipo, datos);
  const correo = normalizarCorreo(email);
  if (!correo) { marcarPendiente(tipo, true); return { enLaNube: false }; }
  try {
    await escribirNube(correo, tipo, datos, false);
    marcarPendiente(tipo, false);
    return { enLaNube: true };
  } catch {
    marcarPendiente(tipo, true);
    return { enLaNube: false };
  }
}
